import React from 'react';

interface OrtsschildProps {
  cityName: string;
  className?: string;
}

/**
 * Clean city name (remove prefixes and suffixes)
 * Must match the cleaning logic in scripts/generate-ortsschilder.js
 */
const cleanCityName = (cityName: string): string => {
  // Remove prefixes like "Gemeinde", "Stadt", etc.
  let cleanName = cityName.replace(/^(Gemeinde|Stadt|Kreisstadt|Große Kreisstadt)\s+/i, '');
  // Remove suffixes like ", Stadt", ", Landeshauptstadt", ", M", ", St", ", Flecken", etc.
  cleanName = cleanName.replace(/,\s*(Landeshauptstadt|Stadt|Freie und Hansestadt|Kreisfreie Stadt|Hansestadt|Universitätsstadt|Residenzstadt|Kreisstadt|Große Kreisstadt|Bergringstadt|Warbelstadt|Barlachstadt|Konfirmationsstadt|Brüder-Grimm-Stadt|Einhardstadt|Liebenbachstadt|Hochschulstadt|documenta-Stadt|Klingenstadt|Kupferstadt|Solestadt|Goethestadt|Welterbestadt|Schliemannstadt|Peenestadt|Vier-Tore-Stadt|Inselstadt|Ostseebad|Seebad|Nordseebad|Ostseeheilbad|Seegemeinde|Klostergemeinde|Point-Alpha-Gemeinde|Nationalparkgemeinde|Nationalparkstadt|gemfr\. Bezirk|Blumenstadt|Windmühlenstadt|Hugenotten- und Waldenserstadt|Reformationsstadt|Schöfferstadt|Barbarossastadt|Bernsteinstadt|Hanse- und Universitätsstadt|Universitäts- und Hansestadt|Flecken|M|St|Markt|Marktgemeinde|Marktflecken).*$/i, '');
  return cleanName.trim();
};

/**
 * Sanitize filename by replacing problematic characters
 * Must match the sanitization logic in scripts/generate-ortsschilder.js
 */
const sanitizeFilename = (name: string): string => {
  return name
    .replace(/\//g, '-')           // Replace forward slashes with hyphens
    .replace(/\\/g, '-')           // Replace backslashes with hyphens
    .replace(/:/g, '-')            // Replace colons with hyphens
    .replace(/\*/g, '-')           // Replace asterisks with hyphens
    .replace(/\?/g, '-')            // Replace question marks with hyphens
    .replace(/"/g, "'")             // Replace double quotes with single quotes
    .replace(/</g, '-')            // Replace less-than with hyphens
    .replace(/>/g, '-')            // Replace greater-than with hyphens
    .replace(/\|/g, '-')           // Replace pipes with hyphens
    .trim();                       // Remove leading/trailing whitespace
};

/**
 * Ortsschild (German place name sign) component
 * 
 * Displays a German-style yellow rectangular place name sign.
 * The image is pre-generated for SEO purposes, but this component
 * can also render an inline SVG as fallback.
 */
const Ortsschild: React.FC<OrtsschildProps> = ({ cityName, className = '' }) => {
  // Clean the city name (remove prefixes and suffixes)
  const cleanName = cleanCityName(cityName);
  
  // Sanitize filename to match generated images (replace / with -, etc.)
  const sanitizedName = sanitizeFilename(cleanName);
  
  // Path to pre-generated PNG (with SEO-friendly prefix)
  const imagePath = `/ortsschild/Einwohnermeldeamt-${sanitizedName}.png`;
  
  // Fallback: render inline SVG if image fails to load
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.warn(`Ortsschild image not found for ${cleanName}, using SVG fallback`);
    
    // Create SVG fallback
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 600" width="800" height="600">
        <rect width="800" height="600" fill="#FFC300" rx="20"/>
        <rect x="10" y="10" width="780" height="580" fill="none" stroke="#000000" stroke-width="20" rx="15"/>
        <text x="400" y="300" font-family="Arial, sans-serif" font-size="100" font-weight="bold" 
              fill="#000000" text-anchor="middle" dominant-baseline="middle">
          ${cleanName}
        </text>
      </svg>
    `;
    
    const svgBlob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(svgBlob);
    e.currentTarget.src = url;
  };

  return (
    <img
      src={imagePath}
      alt={`Einwohnermeldeamt ${cleanName}`}
      className={`ortsschild ${className}`}
      onError={handleImageError}
      loading="eager"
      width="800"
      height="600"
      style={{ maxWidth: '100%', height: 'auto' }}
      itemProp="image"
    />
  );
};

export default Ortsschild;

